// ── Native "block distracting apps" bridge (Android) ────────────────────────
// Wraps the custom AppBlockerPlugin (android/app/.../AppBlockerPlugin.java) +
// AppBlockerService, which together detect (via Usage Access) when a blocked
// app comes to the foreground and draw a "focus session in progress" cover
// over it.
//
// Blocking is driven ONLY by the Clock-In timer (FocusTab) — on while the
// timer/stopwatch runs, including while paused; off only once the session
// truly ends. There used to also be a standalone "Block apps now" Settings
// toggle, independent of the timer — removed deliberately: it undercut the
// whole point of session blocking as a commitment device, since the
// blocklist itself could still be edited to escape it. See
// AppBlockerSection.jsx for how the picker now locks while a session runs.
import { registerPlugin, Capacitor } from '@capacitor/core';

const AppBlocker = registerPlugin('AppBlocker');
const isNative = Capacitor.isNativePlatform();

function requireNative() {
  if (!isNative) throw new Error('App blocker is native-only');
}

export async function isUsageAccessEnabled() {
  requireNative();
  const { enabled } = await AppBlocker.isUsageAccessEnabled();
  return enabled;
}

export async function canDrawOverlays() {
  requireNative();
  const { enabled } = await AppBlocker.canDrawOverlays();
  return enabled;
}

export async function openUsageAccessSettings() {
  requireNative();
  return AppBlocker.openUsageAccessSettings();
}

export async function requestOverlayPermission() {
  requireNative();
  return AppBlocker.requestOverlayPermission();
}

// Installed, launchable apps (icon + label + package name) for the blocklist picker.
export async function getInstalledApps() {
  requireNative();
  const { apps } = await AppBlocker.getInstalledApps();
  return apps;
}

// Fired after the blocklist is saved. Clock-In stays mounted under the
// Settings overlay and otherwise only re-checks on app resume, so without this
// picking apps in Settings left "Block apps for this session" hidden.
export const BLOCKLIST_CHANGED_EVENT = 'clarity-blocklist-changed';

// Persists the chosen blocklist independent of whether blocking is
// currently active — the picker can save selections any time.
export async function setBlockedPackages(packages) {
  requireNative();
  const result = await AppBlocker.setBlockedPackages({ packages });
  try { window.dispatchEvent(new Event(BLOCKLIST_CHANGED_EVENT)); } catch { /* no window */ }
  return result;
}

export async function getBlockedPackages() {
  requireNative();
  const { packages } = await AppBlocker.getBlockedPackages();
  return packages;
}

// Clock-In timer lifecycle only — see file header. `until` (epoch ms, 0 = no
// end) lets native stop blocking on its own when a timer runs out while the
// app is closed; see src/utils/appBlockingState.js.
export async function setSessionBlocking(active, until = 0) {
  requireNative();
  return AppBlocker.setSessionBlocking({ active, until });
}

export async function getBlockingState() {
  requireNative();
  const { sessionActive } = await AppBlocker.getBlockingState();
  return { sessionActive };
}

export { isNative as appBlockerIsNative };
